import CallIcon from "@/assets/icons/lead-page/call-icon";
import MentionIcon from "@/modules/common/components/ui-kit/icons/mention-icon";
import Label from "@/modules/common/components/ui-kit/label";
import { MentionedUser } from "@/modules/lead-page/dtos/index-v2";
import UserNameTag from "@/modules/lead-page/new/components/activity/user-name-tag";
import {
  ColorValue,
  SemanticIndicatorColors,
} from "@/styles/nawy-theme/foundation/colors/semantic";
import {
  Box,
  Typography,
  Button,
  useTheme,
  alpha,
  Popover,
  Stack,
  SxProps,
} from "@mui/material";
import { useState, useRef, useEffect, ReactNode, MouseEvent } from "react";
import { parseLinks } from "@/modules/common/utils/parse-links";

interface CommentBulbProps {
  content: string;
  backgroundColor: ColorValue;
  mentions?: MentionedUser[];
  color?: ColorValue;
  viewMoreSx?: SxProps;
}

const mentionRegex = /@\[([^\]]{1,200})\]\(([^)]{1,200})\)/g;

function parseMentions(
  text: string,
  onClick: (event: MouseEvent<HTMLElement>, uid: string) => void,
  activeUid: string | null
): ReactNode[] {
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = mentionRegex.exec(text)) !== null) {
    const [full, name, uid] = match;
    const start = match.index;

    if (start > lastIndex) {
      nodes.push(...parseLinks(text.slice(lastIndex, start)));
    }

    nodes.push(
      <Box
        key={`m-${nodes.length}`}
        component="span"
        onClick={(e) => onClick(e, uid)}
        sx={(theme) => ({
          display: "inline",
          lineHeight: "inherit",
          fontWeight: 600,
          px: 0.5,
          borderRadius: 0.5,
          color: theme.palette.primary.main,
          bgcolor:
            activeUid === uid
              ? alpha(theme.palette.primary.main, 0.2)
              : alpha(theme.palette.primary.main, 0.1),
          cursor: "pointer",
          transition: "background 0.2s ease",
          "&:hover": {
            bgcolor: alpha(theme.palette.primary.main, 0.25),
            textDecoration: "underline",
          },
        })}
      >
        @{name}
      </Box>
    );

    lastIndex = start + full.length;
  }

  if (lastIndex < text.length) {
    nodes.push(...parseLinks(text.slice(lastIndex)));
  }

  return nodes;
}

const CommentBulb = ({
  content,
  backgroundColor,
  color,
  mentions = [],
  viewMoreSx,
}: CommentBulbProps) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const [showToggle, setShowToggle] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedUser, setSelectedUser] = useState<MentionedUser | null>(null);

  const handleMentionClick = (event: MouseEvent<HTMLElement>, uid: string) => {
    if (anchorEl === event.currentTarget) {
      setAnchorEl(null);
      setSelectedUser(null);
      return;
    }

    const user = mentions.find((m) => m.uid === uid);
    if (user) {
      setSelectedUser(user);
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelectedUser(null);
  };

  useEffect(() => {
    if (contentRef.current) {
      const lineHeight =
        parseFloat(getComputedStyle(contentRef.current).lineHeight || "1.5") ||
        1.5;
      const maxLines = 2;
      const maxHeight = lineHeight * maxLines;

      requestAnimationFrame(() => {
        if (!contentRef.current) return;
        if (contentRef.current.scrollHeight > maxHeight + 1) {
          setShowToggle(true);
        } else {
          setShowToggle(false);
        }
      });
    }
  }, [content]);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          width: "100%",
          padding: `${theme.nawy.spacing.xs} ${theme.nawy.spacing.sm}`,
          borderRadius: `0 ${theme.nawy.radius.xl} ${theme.nawy.radius.xl} ${theme.nawy.radius.xl}`,
          background:
            backgroundColor ?? theme.nawy.colors.semantic.layer.secondary,
        }}
      >
        <Typography
          ref={contentRef}
          sx={{
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            WebkitLineClamp: expanded ? ("unset" as any) : 2,
            wordBreak: "break-word",
            overflowWrap: "anywhere",
            whiteSpace: "pre-wrap",
            ...theme.nawy.typography.english.variants.body.md.regular,
            color: color ?? theme.nawy.colors.semantic.text.primary,
          }}
        >
          {parseMentions(
            content,
            handleMentionClick,
            selectedUser?.uid || null
          )}
        </Typography>

        {showToggle && (
          <Button
            onClick={() => setExpanded((prev) => !prev)}
            sx={{
              mt: 0.5,
              p: 0,
              fontSize: "0.75rem",
              textTransform: "none",
              ...viewMoreSx,
            }}
          >
            {expanded ? "View less" : "View more"}
          </Button>
        )}
      </Box>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        PaperProps={{
          sx: {
            padding: theme.nawy.spacing.md,
            borderRadius: theme.nawy.radius.xl,
            boxShadow: theme.nawy.elevation.sm,
            backgroundColor: theme.nawy.colors.semantic.layer.primary,
            minWidth: 260,
            maxWidth: 300,
          },
        }}
        disableRestoreFocus
      >
        {selectedUser && (
          <Stack
            direction="column"
            spacing={theme.nawy.spacing.md}
            alignItems="flex-start"
          >
            <Stack
              width="100%"
              direction="row"
              justifyContent="space-between"
              spacing={theme.nawy.spacing.xxs}
            >
              <UserNameTag
                name={selectedUser.name}
                color={selectedUser.color}
              />

              <Label
                label={selectedUser.rank}
                variant="outlined"
                color={selectedUser.color as SemanticIndicatorColors}
              />
            </Stack>

            <Stack
              direction="row"
              spacing={theme.nawy.spacing.sm}
              alignItems="center"
            >
              <MentionIcon />
              <Typography
                noWrap
                sx={{
                  ...theme.nawy.typography.english.variants.body.sm.regular,
                  color: theme.nawy.colors.semantic.text.primary,
                  maxWidth: 200,
                }}
              >
                {selectedUser.email}
              </Typography>
            </Stack>

            <Stack
              direction="row"
              spacing={theme.nawy.spacing.sm}
              alignItems="center"
            >
              <CallIcon />
              <Typography
                noWrap
                sx={{
                  ...theme.nawy.typography.english.variants.body.sm.regular,
                  color: theme.nawy.colors.semantic.text.primary,
                  maxWidth: 200,
                }}
              >
                {selectedUser.phoneNumber}
              </Typography>
            </Stack>
          </Stack>
        )}
      </Popover>
    </>
  );
};

export default CommentBulb;
